import { set } from 'lodash';
import { transformElementDefaults } from 'common/utils/facades/utils';
import CARD_FACADES, { FacadeValues } from 'common/utils/facades/card-facades';
import { CardData, StorySettingsType } from 'types/story';
import { CARD_TYPE } from 'common/constants';

class CardFacade {
	static getCardNameByType(type: CardData['type']): string {
		const nameAlias = {
			[CARD_TYPE.PERSONALITY_TEST]: 'PERSONALITY TEST',
			[CARD_TYPE.TRUE_OR_FALSE]: 'TRUE OR FALSE',
			[CARD_TYPE.THIS_OR_THAT]: 'THIS OR THAT',
			[CARD_TYPE.FORM]: 'LEADS FORM',
		};

		return nameAlias[type] || type;
	}

	card: FacadeValues;

	/**
	 * @param card {Object} Card object
	 */
	constructor(card: CardData) {
		this.card = new CARD_FACADES[card.type](card as CardData);
	}

	get type() {
		return this.card.data.type;
	}

	get events() {
		return this.card.data.events;
	}

	get elements() {
		return this.card.data.elements;
	}

	setElements(elements) {
		return set(this, 'card.data.elements', elements);
	}

	get data() {
		return this.card.data;
	}

	get cardId() {
		return this.card.data._id;
	}

	get hasFeature() {
		return {
			results: this.type === CARD_TYPE.POLL || this.type === CARD_TYPE.THIS_OR_THAT,
			correct: this.type === CARD_TYPE.TRIVIA || this.type === CARD_TYPE.TRUE_OR_FALSE,
			score: this.type === CARD_TYPE.TRIVIA || this.type === CARD_TYPE.TRUE_OR_FALSE,
			answer:
				this.type === CARD_TYPE.TRIVIA ||
				this.type === CARD_TYPE.POLL ||
				this.type === CARD_TYPE.TRUE_OR_FALSE ||
				this.type === CARD_TYPE.THIS_OR_THAT ||
				this.type === CARD_TYPE.PERSONALITY_TEST,
		};
	}

	getShowCorrect(storySettings: StorySettingsType) {
		if (storySettings.cards?.[this.cardId]?.showCorrect !== undefined) {
			return storySettings.cards[this.cardId].showCorrect;
		}

		// fallback to old json
		return this.hasFeature.correct;
	}

	getShowResults(storySettings: StorySettingsType) {
		if (storySettings.cards?.[this.cardId]?.showResults !== undefined) {
			return storySettings.cards[this.cardId].showResults;
		}

		// fallback to old json
		return this.hasFeature.results;
	}

	/**
	 * Transform configurable defaults of every element in card
	 * relatively to mediaQuery config of Story
	 *
	 * @param mediaQuery {Object} StoryFacade.getMediaQuery
	 */
	transformElementsDefaults({ mediaQuery }) {
		const { defaultPlatform } = mediaQuery;
		let { elements } = this;

		elements = transformElementDefaults(defaultPlatform, elements) as CardData['elements'];

		this.setElements(elements);
	}
}

export { CardFacade };
